import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const stats = [
    { number: '400+', label: 'DSA Problems Solved' },
{ number: '4', label: 'MERN Stack Projects' },
{ number: '6+', label: 'Frontend Applications Built' },

  ];

  return (
    <section id="about" className="section-padding relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent-primary opacity-5 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-secondary opacity-5 rounded-full blur-3xl animate-float-2" />

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-6xl mx-auto relative z-10"
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <motion.p
            className="text-sm text-accent-primary font-semibold tracking-[3px] mb-4 inline-block"
            whileHover={{ scale: 1.1, letterSpacing: '5px' }}
            transition={{ duration: 0.3 }}
          >
            ABOUT ME
          </motion.p>

          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black gradient-text mb-6">
            Who I Am
          </h2>

          <p className="text-lg sm:text-xl text-text-secondary font-light max-w-2xl mx-auto">
            A developer who enjoys building things that actually work and scale
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div variants={itemVariants} className="max-w-3xl mx-auto mb-16">
          <div className="glass-effect p-8 rounded-2xl text-left space-y-6 hover-lift">
            <h3 className="text-2xl sm:text-3xl font-bold text-accent-primary mb-6 flex items-center gap-3">
              <motion.span
                className="w-2 h-2 bg-accent-primary rounded-full"
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
              Software Developer
            </h3>

            <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
              I’m <span className="text-accent-primary font-semibold">Pavani Kuruva</span>, a software developer focused on building clean, reliable, and scalable web applications. I work primarily with the MERN stack and enjoy designing backend systems that are secure, efficient, and easy to maintain.
            </p>

            <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
              I’m currently pursuing a B.Tech in Electronics and Communication Engineering at IIIT Tiruchirappalli. Alongside academics, I’ve solved over <span className="text-accent-primary font-semibold">400+ DSA problems in Java</span>, which has strengthened my problem-solving skills and understanding of core computer science concepts.
            </p>

            <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
              I enjoy turning ideas into real products, collaborating with others, and continuously improving my skills by learning new tools, frameworks, and best practices in modern web development.
            </p>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                y: -10,
                scale: 1.05,
                boxShadow: '0 20px 50px rgba(76, 201, 240, 0.3)',
              }}
              className="text-center p-8 glass-effect rounded-2xl transition-smooth group relative overflow-hidden"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-accent-primary to-accent-secondary opacity-0 group-hover:opacity-10 transition-opacity duration-500"
              />

              <motion.div
                className="text-4xl sm:text-5xl font-bold text-accent-primary mb-2 relative z-10"
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : { scale: 0 }}
                transition={{
                  delay: index * 0.2,
                  type: 'spring',
                  stiffness: 200,
                  damping: 15,
                }}
              >
                {stat.number}
              </motion.div>

              <div className="text-text-secondary text-sm tracking-wider uppercase relative z-10">
                {stat.label}
              </div>

              <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-accent-primary opacity-30 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-accent-primary opacity-30 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About;
